import { getFromLocalStorage, saveInLocalStorage } from '@/utils/local-storage';
import { defineStore } from 'pinia';
import pusherService from '@/services/pusher';
import notificationManager from '@/services/notificationManager';

const initialState = {
    isLoggedIn: getFromLocalStorage('isLoggedIn', false),
    user: getFromLocalStorage('user', null),
    token: getFromLocalStorage('token', null),
    sessionChecked: false
}

export const useAuthStore = defineStore('authStore', {
    state: () => initialState,

    getters: {},

    actions: {
        setIsLoggedIn(value) {
            this.isLoggedIn = value;
            saveInLocalStorage('isLoggedIn', value);
        },

        setToken(value) {
            this.token = value;
            saveInLocalStorage('token', value);
        },

        setRoles(value) {
            this.roles = value;
            saveInLocalStorage('roles', value);
        },

        setUser(value, isLoggedIn = null) {
            this.user = value;
            saveInLocalStorage('user', value);

            if(isLoggedIn != null) 
                this.setIsLoggedIn(isLoggedIn);
        },

        async logout() {
            try {
                // Clean up connections before clearing user data
                await this.disconnectServices();
                
                this.setIsLoggedIn(false);
                this.setRoles([]);
                this.setUser(null);
                this.setToken(null);
            } catch (error) {
                console.error('Error during logout cleanup:', error);
                // Still proceed with logout even if cleanup fails
                this.setIsLoggedIn(false);
                this.setRoles([]);
                this.setUser(null);
                this.setToken(null);
            }
        },

        /**
         * Initialize real-time connections for authenticated user
         */
        async initializeConnections() {
            if (!this.user || !this.user.id) {
                console.warn('Cannot initialize connections: no user data');
                return;
            }

            try {
                console.log('Initializing connections for user:', this.user.id);
                
                // Connect to Pusher first
                await pusherService.connect(this.user.id);
                
                // Then initialize notification manager
                await notificationManager.init(this.user.id);
                
                console.log('Connections initialized successfully');
            } catch (error) {
                console.error('Error initializing connections:', error);
                throw error;
            }
        },

        /**
         * Disconnect and cleanup all services
         */
        async disconnectServices() {
            try {
                console.log('Disconnecting services...');
                
                // Clean up notification manager first
                await notificationManager.cleanup();
                
                // Then disconnect Pusher
                await pusherService.disconnect();
                
                // Clear online users from user store if available
                const { useUserStore } = await import('@/stores/user');
                const userStore = useUserStore();
                if (userStore.clearOnlineUsers) {
                    userStore.clearOnlineUsers();
                }
                
                console.log('Services disconnected successfully');
            } catch (error) {
                console.error('Error disconnecting services:', error);
                throw error;
            }
        },

        setSessionCheck(value) {
            this.sessionChecked = value
        }
    }
});